import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { Panel } from "@/components/Panel";
import { useAuth } from "@/api/auth";
import { useRollenPostfach, type ArbeitsplatzSchrittStatus, type PostfachEintrag, type PostfachRolle } from "@/api/purchase";
import { formatUsd, orderStatusLabel } from "@/lib/purchase-format";
import { fmtEUR } from "@/lib/pricing-format";
import type { KapitalBestellung } from "./kapital";
import { schraffur } from "./kapital-darstellung";
import {
  LAUFZETTEL_BESTELLUNG, SCHRITT_TEXTE, STATUS_TEXTE, codeText, kostenErwartetText, laufzettelZeichen, laufzettelZustand,
  ohneKostenpostenText, schrittText, statusChipKlasse,
} from "./arbeitsplatz/schritte";

/**
 * Rollen-Postfach (Plan 2026-10-19): „Was ist jetzt dran“ ueber alle nicht archivierten
 * Bestellungen. Der Server liefert je offener Spur Schritt, Codes und Rolle — hier wird nur
 * gruppiert und beschriftet (Texte aus arbeitsplatz/schritte.ts). Kapitalstand optional aus der Bestellliste.
 * Karten im Klartext (21.10.2026): Kopf mit Lieferant/Datum/Status, Laufzettel aller Schritte
 * (✓/✗), Kosten-Grund mit Posten und ein direkter Weg in den Reiter Zahlungen der Akte.
 */

type Rechte = Record<string, boolean | undefined>;
type KapitalMap = ReadonlyMap<number, KapitalBestellung>;

const GRUPPEN: Array<{ rolle: PostfachRolle; titel: string; meine: (p: Rechte) => boolean }> = [
  { rolle: "einkauf", titel: "Einkauf", meine: (p) => !!p.article_purchase },
  { rolle: "lagerleitung", titel: "Lagerleitung", meine: (p) => !!(p.warehouse_lead || p.warehouse_admin) },
  { rolle: "admin_preise", titel: "Admin/Preise", meine: (p) => !!(p.article_purchase_prices || p.article_purchase_push) },
];

const wartet = (e: PostfachEintrag) => e.schritt.status === "wait" || e.schritt.status === "gate";

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

// „Container anlegen“ ist keine Spur der Akte: Link ohne ?spur=, die Akte zeigt dort den Knopf.
const ohneContainer = (e: PostfachEintrag) => e.schritt.key === "container_anlegen";

function spurTitel(e: PostfachEintrag): string {
  if (ohneContainer(e)) return "Ohne Container";
  if (e.container_nr === null) return "Bestellung";
  return `C${e.container_nr} · ${e.container_no ?? e.label ?? `#${e.shipment_id}`}`;
}

const LAUFZETTEL_FARBE: Record<ArbeitsplatzSchrittStatus, string> = {
  done: "text-success",
  now: "text-primary",
  fix: "text-coral",
  wait: "text-warn",
  gate: "text-ink-2",
  idle: "text-ink-2/70",
};

interface LaufzettelFeld { key: string; kurz: string; status: ArbeitsplatzSchrittStatus }

/** Eine Zeile Laufzettel: Feld fuer Feld abgehakt. Das Feld, um das es auf der Karte geht, ist hinterlegt. */
function Laufzettel({ titel, felder, aktuell }: { titel: "Container" | "Bestellung"; felder: LaufzettelFeld[]; aktuell: string }) {
  return (
    <span className="flex items-baseline gap-2">
      <span aria-hidden className="w-[4.5rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-ink-2">{titel}</span>
      <ol aria-label={`Laufzettel ${titel}`} className="flex min-w-0 flex-wrap gap-x-1 gap-y-0.5">
        {felder.map((f) => (
          <li
            key={f.key}
            aria-label={`${f.kurz}: ${laufzettelZustand(f.status, titel === "Bestellung")}`}
            className={`inline-flex items-baseline gap-1 rounded px-1 text-xs ${LAUFZETTEL_FARBE[f.status]} ${
              f.key === aktuell ? "bg-panel-2 font-semibold ring-1 ring-line" : ""
            }`}
          >
            <span aria-hidden className="font-semibold">{laufzettelZeichen(f.status)}</span>
            <span aria-hidden className={f.status === "done" || f.status === "idle" ? "text-ink-2" : "text-ink"}>{f.kurz}</span>
          </li>
        ))}
      </ol>
    </span>
  );
}

/** Gruende der Karte: Kostencodes mit den konkreten Posten, sonst die Saetze aus schritte.ts. */
function gruende(e: PostfachEintrag): string[] {
  // Gleicher Code mehrfach (z. B. je Position) -> ein Satz genuegt.
  return [...new Set(e.schritt.codes.map((c) => c.code))].map((code) => {
    if (code === "kosten_erwartet" && e.kosten_erwartet?.length) return kostenErwartetText(e.kosten_erwartet, e.container_nr === null);
    if (code === "kosten_fehlen" && e.ohne_kostenposten?.length) return ohneKostenpostenText(e.ohne_kostenposten);
    return codeText(code);
  });
}

type AktenReiter = "zahlungen" | "container";

/** Direkter Weg in die Akte (Reiter Zahlungen bzw. Container) — nur mit Preisrecht, das die Felder dort braucht. */
function wegeInDieAkte(e: PostfachEintrag): Array<{ label: string; reiter: AktenReiter }> {
  const offen = (key: string) => {
    const s = e.bestell_schritte?.find((x) => x.key === key);
    return s !== undefined && s.status !== "done";
  };
  const out: Array<{ label: string; reiter: AktenReiter }> = [];
  if (offen("invoice") && offen("zahlungen")) out.push({ label: "Rechnung & Zahlung erfassen", reiter: "zahlungen" });
  else if (offen("invoice")) out.push({ label: "Rechnungssumme erfassen", reiter: "zahlungen" });
  else if (offen("zahlungen")) out.push({ label: "Zahlung erfassen", reiter: "zahlungen" });
  if (offen("kosten")) out.push({ label: "Kosten erfassen", reiter: "container" });
  return out;
}

function Zahlstreifen({ row }: { row: KapitalBestellung }) {
  const anteil = row.bezahlt_anteil;
  const hatZahlung = row.bezahlt_usd > 0 || row.bezahlt_eur > 0;
  const positionenOhneWert = row.quelle === "proforma" ? row.positions_ohne_preis
    : row.quelle === "ek" ? row.positions_ohne_ek : 0;
  const wertUnvollstaendig = row.wert_unvollstaendig;
  if (anteil === null && !hatZahlung && !wertUnvollstaendig) return null;
  const keinKurs = row.quelle !== null && row.bezahlt_quelle === null;
  // Solange etwas offen oder der Wert unvollständig ist, nie „100 %“ zeigen.
  const prozent = anteil === null ? null : anteil < 1 || wertUnvollstaendig ? Math.min(99, Math.round(anteil * 100)) : 100;
  const balkenAnteil = wertUnvollstaendig ? Math.min(0.99, anteil ?? 0) : anteil ?? 0;
  const erfasst = [row.bezahlt_usd > 0 ? formatUsd(row.bezahlt_usd) : null,
    row.bezahlt_eur > 0 ? fmtEUR(row.bezahlt_eur) : null].filter((betrag) => betrag !== null).join(" · ");
  const bezahlt = row.bezahlt_quelle !== null
    ? `${row.bezahlt_quelle_umgerechnet ? "≈ " : ""}${row.geschaetzt ? fmtEUR(row.bezahlt_quelle) : formatUsd(row.bezahlt_quelle)}`
    : keinKurs ? erfasst : row.bezahlt_usd > 0 ? formatUsd(row.bezahlt_usd) : fmtEUR(row.bezahlt_eur);
  const mind = wertUnvollstaendig ? "mind. " : "";

  return (
    <span className="mt-2 block space-y-1 text-xs tabular-nums text-ink-2">
      {anteil !== null && (
        <span role="progressbar" aria-label="Bezahlt" aria-valuenow={prozent!} aria-valuemin={0} aria-valuemax={100}
          className="flex h-1.5 overflow-hidden rounded-full bg-panel-2">
          <span className="bg-success" style={{ width: `${balkenAnteil * 100}%` }} />
          <span className={row.geschaetzt ? "bg-panel-2" : "bg-success/25"}
            style={{ ...(row.geschaetzt ? schraffur : {}), width: `${(1 - balkenAnteil) * 100}%` }} />
        </span>
      )}
      <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="text-ink">{keinKurs ? `Bezahlt ${bezahlt}` : !hatZahlung ? "Noch nichts bezahlt"
            : anteil === null ? `Bezahlt ${bezahlt} · Bestellwert unbekannt` : `Bezahlt ${prozent} % · ${bezahlt}`}</span>
          {row.geschaetzt && <span className="rounded border border-line px-1.5 text-[11px] text-ink-2">ca. EK</span>}
        </span>
        {anteil !== null && (anteil < 1 || wertUnvollstaendig) && (
          <span className="ml-auto text-right">{row.geschaetzt ? `offen ${mind}ca. ${fmtEUR(row.offen_eur)}`
            : `offen ${mind}${formatUsd(row.offen_usd)}${row.offen_eur !== null ? ` ≈ ${fmtEUR(row.offen_eur)}` : ""}`}</span>
        )}
      </span>
      {keinKurs && <span className="block text-warn">Anteil unbekannt – kein Kurs</span>}
      {wertUnvollstaendig && (
        <span className="block text-warn">Wert unvollständig{positionenOhneWert > 0
          ? ` · ${positionenOhneWert} Pos. ohne ${row.geschaetzt ? "EK" : "Preis"}` : ""}</span>
      )}
      {!wertUnvollstaendig && !keinKurs && row.ueberzahlt !== null && row.ueberzahlt > 0 && (
        <span className="block text-warn">Überzahlt um {row.geschaetzt ? fmtEUR(row.ueberzahlt) : formatUsd(row.ueberzahlt)}</span>
      )}
    </span>
  );
}

function PostfachZeile({ e, darfPreise, kapital }: { e: PostfachEintrag; darfPreise: boolean; kapital?: KapitalBestellung }) {
  const text = schrittText(e.schritt);
  const spurFelder: LaufzettelFeld[] = (e.spur_schritte ?? [])
    .map((x) => ({ key: x.key, kurz: SCHRITT_TEXTE[x.key]?.kurz ?? x.key, status: x.status }));
  const bestellFelder: LaufzettelFeld[] = LAUFZETTEL_BESTELLUNG.flatMap(({ key, kurz }) => {
    const s = e.bestell_schritte?.find((x) => x.key === key);
    return s ? [{ key, kurz, status: s.status }] : [];
  });
  const wege = darfPreise ? wegeInDieAkte(e) : [];
  const kopf = [
    e.order_date ? `bestellt am ${fmtDate(e.order_date)}` : null,
    e.order_status
      ? `${orderStatusLabel(e.order_status)}${e.container_anzahl !== undefined ? ` · ${e.container_anzahl} Container` : ""}`
      : null,
    e.eta_door ? `ETA ${fmtDate(e.eta_door)}` : null,
  ].filter((x): x is string => x !== null);
  return (
    <li className="overflow-hidden rounded-md border border-line bg-panel">
      <Link
        to={ohneContainer(e) ? `/einkauf/${e.order_id}` : `/einkauf/${e.order_id}?spur=${encodeURIComponent(e.spur_key)}`}
        className="block px-3 py-2 hover:bg-panel-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      >
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-ink">{e.order_number} · {spurTitel(e)}</span>
            <span className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-xs text-ink-2">
              <span className="font-semibold text-ink">{e.supplier_name ?? "Ohne Lieferant"}</span>
              {kopf.map((k) => (
                <span key={k} className="contents">
                  <span aria-hidden>·</span>
                  <span>{k}</span>
                </span>
              ))}
            </span>
          </span>
          <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium ${statusChipKlasse(e.schritt.status)}`}>
            {STATUS_TEXTE[e.schritt.status]}
          </span>
        </span>
        <span className="mt-1 block text-xs text-ink-2">
          Jetzt: <span className="text-ink">{text.kurz} · {text.aufgabe}</span>
        </span>
        {(spurFelder.length > 0 || bestellFelder.length > 0) && (
          <span className="mt-1.5 block space-y-0.5 border-t border-dashed border-line pt-1.5">
            {spurFelder.length > 0 && <Laufzettel titel="Container" felder={spurFelder} aktuell={e.schritt.key} />}
            {bestellFelder.length > 0 && <Laufzettel titel="Bestellung" felder={bestellFelder} aktuell={e.schritt.key} />}
          </span>
        )}
        {darfPreise && kapital && <Zahlstreifen row={kapital} />}
        {e.schritt.codes.length > 0 && (
          <span className="mt-1.5 block space-y-0.5 text-xs text-ink">
            {gruende(e).map((satz) => <span key={satz} className="block">{satz}</span>)}
          </span>
        )}
      </Link>
      {wege.length > 0 && (
        <span className="flex flex-wrap gap-x-3 gap-y-1 border-t border-line bg-panel-2/50 px-3 py-1.5">
          {wege.map((w) => (
            <Link
              key={w.label}
              to={`/einkauf/${e.order_id}?spur=bestellung&reiter=${w.reiter}`}
              className="inline-flex items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {w.label}
              <ArrowRight aria-hidden className="size-3" />
            </Link>
          ))}
        </span>
      )}
    </li>
  );
}

function Gruppe({ titel, meine, eintraege, darfPreise, kapital }: {
  titel: string; meine: boolean; eintraege: PostfachEintrag[]; darfPreise: boolean; kapital?: KapitalMap;
}) {
  const [offen, setOffen] = useState(false);
  const dringend = eintraege.filter((e) => !wartet(e));
  const warten = eintraege.filter(wartet);
  return (
    <section aria-label={titel} className="rounded-md border border-line bg-panel-2/40 p-3">
      <header className="mb-2 flex items-center gap-2">
        <h4 className="font-display text-sm font-semibold text-ink">{titel}</h4>
        <span className="rounded-full bg-panel-2 px-2 py-0.5 text-xs tabular-nums text-ink-2">{eintraege.length}</span>
        {meine && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">Meine</span>}
      </header>
      {dringend.length > 0 ? (
        <ul className="grid gap-2">{dringend.map((e) => <PostfachZeile key={`${e.order_id}-${e.spur_key}`} e={e} darfPreise={darfPreise} kapital={kapital?.get(e.order_id)} />)}</ul>
      ) : (
        <p className="text-xs text-ink-2">{warten.length > 0 ? "Nichts dringend." : "Nichts dran."}</p>
      )}
      {warten.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            aria-expanded={offen}
            onClick={() => setOffen((v) => !v)}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1 py-0.5 text-xs font-medium text-ink-2 hover:text-ink"
          >
            {offen ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
            Wartet ({warten.length})
          </button>
          {offen && (
            <ul className="mt-2 grid gap-2">{warten.map((e) => <PostfachZeile key={`${e.order_id}-${e.spur_key}`} e={e} darfPreise={darfPreise} kapital={kapital?.get(e.order_id)} />)}</ul>
          )}
        </div>
      )}
    </section>
  );
}

export function RollenPostfach({ kapital }: { kapital?: KapitalMap } = {}) {
  const { data, isLoading, isError } = useRollenPostfach();
  const { user } = useAuth();
  const rechte: Rechte = user?.permissions ?? {};
  // Ein aelteres Backend kennt die Route nicht: dann gibt es keine Eintraege.
  const eintraege = data?.eintraege ?? [];
  // "Meine" zuerst; innerhalb gleicher Zugehoerigkeit bleibt die feste Reihenfolge.
  const gruppen = GRUPPEN.map((g) => ({ ...g, istMeine: g.meine(rechte) }))
    .sort((a, b) => Number(b.istMeine) - Number(a.istMeine));

  return (
    <Panel title="Was ist jetzt dran" subtitle="Rollen-Postfach — je offener Spur der nächste Schritt, nach Rolle">
      {isError ? (
        <div className="py-4 text-center text-sm text-coral">Postfach nicht erreichbar — fehlt die Einkaufs-Berechtigung?</div>
      ) : isLoading ? (
        <div className="py-4 text-center text-sm text-ink-2">Lädt…</div>
      ) : (
        <>
          {data?.abgeschnitten && (
            <p role="status" className="mb-3 text-xs text-warn">
              Nur die neuesten 500 Bestellungen berücksichtigt — ältere fehlen hier.
            </p>
          )}
          {eintraege.length === 0 ? (
            <div className="py-4 text-center text-sm text-ink-2">Nichts dran.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {gruppen.map((g) => (
                <Gruppe key={g.rolle} titel={g.titel} meine={g.istMeine} darfPreise={!!rechte.article_purchase_prices} kapital={kapital}
                  eintraege={eintraege.filter((e) => e.rolle === g.rolle)} />
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
